import {
  createContext,
  createElement,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ComponentType,
  type ReactNode,
} from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { toast } from 'react-toastify';
import { AxiosError } from 'axios';

import type { Role } from '../../../../types/role';
import RoleService from '../../../../services/roleService';
import { useAuth } from '../../../../context/AuthContext';
import {
  usePermissionsConfig,
  type PermissionsConfig,
} from '../../../../hooks/usePermissionsConfig';
import {
  sanitizePermissions,
  summarizeStripped,
} from '../../utils/sanitizePermissions';
import { TIER_AUTO_GRANTS } from '../EpcProjectsTierAutoGrant';

import { RoleEditorContext } from './RoleEditorContext';
import type {
  ApprovalChainState,
  DeptKey,
  RoleEditorContextValue,
  RoleEditorFormValues,
  RoleEditorSection,
} from './types';

// ---------------------------------------------------------------------------
// Legacy permission-config shape kept for parity with the old RoleForm. The
// outer code only consumes it to drive the "limited access" UI counters; the
// real matrix renders from the new shape (matrixConfig).
// ---------------------------------------------------------------------------
interface LegacyPermissionConfig {
  modules: Array<{ key: string; name: string; departmentType?: string }>;
  actions: Array<{ key: string; name: string }>;
}

// ---------------------------------------------------------------------------
// Yup schema — replicated from RoleForm.tsx exactly. The `permissions` test
// asserts that at least one module has a non-empty selection array.
// ---------------------------------------------------------------------------
const schema = yup.object({
  name: yup
    .string()
    .required('Role name is required')
    .min(2, 'Role name must be at least 2 characters'),
  description: yup
    .string()
    .optional()
    .max(200, 'Description must be less than 200 characters'),
  permissions: yup
    .object()
    .test('permissions-required', 'At least one permission must be selected', function (value) {
      if (!value) return false;
      const hasPermissions = Object.values(value).some(
        (permissions: unknown) => Array.isArray(permissions) && permissions.length > 0,
      );
      return hasPermissions;
    }),
});

// ---------------------------------------------------------------------------
// Command context — exposes submit() and assorted top-level UI state to
// header / footer siblings without prop-drilling. Kept private to this file.
// ---------------------------------------------------------------------------
interface RoleEditorCommandValue {
  submit: () => Promise<void>;
  isSubmitting: boolean;
  isDirty: boolean;
  apiError: string | null;
  clearApiError: () => void;
  showSuccess: boolean;
  postSavePrompt: boolean;
  setPostSavePrompt: (v: boolean) => void;
  savedRole: Role | null;
  setActiveSection: (s: RoleEditorSection) => void;
  activeSection: RoleEditorSection;
  formTitle: string;
  editingRole: Role | null | undefined;
}

const RoleEditorCommandContext = createContext<RoleEditorCommandValue | null>(null);

export function useRoleEditor(): RoleEditorCommandValue {
  const ctx = useContext(RoleEditorCommandContext);
  if (!ctx) {
    throw new Error(
      'useRoleEditor must be used inside <RoleEditorProvider>. ' +
        'Wrap the consumer in a <RoleEditor> (which mounts RoleEditorProvider).',
    );
  }
  return ctx;
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------
export interface RoleEditorProviderProps {
  editingRole?: Role | null;
  propDepartment?: string;
  onAdd: (role: Role) => void;
  onClose?: () => void;
  children: ReactNode;
}

export function RoleEditorProvider({
  editingRole,
  propDepartment = 'OMS',
  onAdd,
  onClose: _onClose,
  children,
}: RoleEditorProviderProps) {
  // Department is managed internally so the user can change it within the form.
  const [activeDept, setActiveDept] = useState<DeptKey>(
    (editingRole?.department as DeptKey) || (propDepartment as DeptKey) || 'OMS',
  );

  const [apiError, setApiError] = useState<string | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);

  // Approval workflow state — mirrors the original RoleForm's useState slots.
  const [approverRoles, setApproverRoles] = useState<Role[]>([]);
  const [selectedRoleType, setSelectedRoleType] = useState<'approver' | 'requester' | 'both'>(
    'both',
  );
  const [selectedApproverRole, setSelectedApproverRole] = useState<string>('');
  const [isMobileUser, setIsMobileUser] = useState<boolean>(false);
  const [savedRole, setSavedRole] = useState<Role | null>(null);
  const [postSavePrompt, setPostSavePrompt] = useState(false);

  // New: tracks which section is currently active in the composable editor.
  const [activeSection, setActiveSection] = useState<RoleEditorSection>('identity');

  // ── Permissions config via React Query hook (replaces inline service call).
  const {
    data: rawPermissionsConfig,
    isLoading: isLoadingConfig,
    error: permissionConfigError,
  } = usePermissionsConfig(activeDept);

  // Current user's role & permission predicate, used to filter matrixConfig.
  const { role: userRole, hasPermission } = useAuth();

  // ── Legacy adapter: flatten per-module actions into a global action union
  // so old counter / invalid-module code keeps working. Copied verbatim from
  // RoleForm.tsx.
  const permissionsConfig = useMemo<LegacyPermissionConfig | null>(() => {
    if (!rawPermissionsConfig) return null;
    const actionKeys = new Set<string>();
    for (const m of rawPermissionsConfig.modules) {
      for (const a of m.actions) actionKeys.add(a);
    }
    for (const a of rawPermissionsConfig.actionCatalog) actionKeys.add(a.key);
    const actionByKey = new Map(
      rawPermissionsConfig.actionCatalog.map((a) => [a.key, a]),
    );
    return {
      modules: rawPermissionsConfig.modules.map((m) => ({
        key: m.key,
        name: m.name,
        departmentType: undefined,
      })),
      actions: Array.from(actionKeys).map((key) => ({
        key,
        name: actionByKey.get(key)?.name ?? key,
      })),
    };
  }, [rawPermissionsConfig]);

  // ── Role-filtered matrix config: drop (module, action) pairs the current
  // user cannot grant, and modules where every action is ungrantable. Copied
  // verbatim from RoleForm.tsx.
  const matrixConfig = useMemo<PermissionsConfig | null>(() => {
    if (!rawPermissionsConfig) return null;
    const isAdmin = userRole?.toLowerCase() === 'admin';
    const filteredModules = rawPermissionsConfig.modules
      .map((m) => {
        const grantable = m.actions.filter((a) => (isAdmin ? true : hasPermission(m.key, a)));
        return { ...m, actions: grantable };
      })
      .filter((m) => m.actions.length > 0);
    return {
      modules: filteredModules,
      actionCatalog: rawPermissionsConfig.actionCatalog,
    };
  }, [rawPermissionsConfig, userRole, hasPermission]);

  // Toast on config-load failure (preserves the previous UX).
  useEffect(() => {
    if (permissionConfigError) {
      console.error('Failed to fetch permission config:', permissionConfigError);
      toast.error('Failed to load permission configuration');
    }
  }, [permissionConfigError]);

  // ── React-hook-form setup.
  const form = useForm<RoleEditorFormValues>({
    // The original cast was `as any` because yup's resolver type is wider
    // than RHF's resolver generic with a Yup `object()` schema. Preserved.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: yupResolver(schema) as any,
    defaultValues: {
      name: '',
      description: '',
      permissions: {},
      isMobileUserRole: false,
    } as RoleEditorFormValues,
    mode: 'onChange',
  });

  const {
    handleSubmit,
    formState: { isSubmitting, isDirty },
    reset,
    setValue,
    setFocus,
    control,
    getValues,
  } = form;

  // ── Auto-sync: when the EPC tier tag changes (Step 2 picker), overwrite
  // permissions.epcProject with the canonical tier auto-grant set. Keeps the
  // Permissions step honest without forcing the admin to click through the
  // matrix a second time. Per the design note: Advanced disclosure overrides
  // are wiped on every tag change (acceptable trade-off).
  const watchedTag = useWatch({ control, name: 'tag' });
  useEffect(() => {
    // Only auto-grant for EPC roles. Other departments don't use the epcProject module.
    if (activeDept !== 'EPC') return;
    const current = getValues('permissions') ?? {};
    if (!watchedTag) {
      // No tag picked — strip any stale auto-grants on the epcProject module
      // so we don't smuggle workspace flags out of the editor.
      if (Array.isArray(current.epcProject) && current.epcProject.length > 0) {
        setValue(
          'permissions',
          { ...current, epcProject: [] },
          { shouldDirty: true, shouldValidate: true },
        );
      }
      return;
    }
    const desired = (TIER_AUTO_GRANTS[watchedTag] ?? []).map((g) => g.action);
    const existing = Array.isArray(current.epcProject) ? current.epcProject : [];
    // Cheap equality: same length + every desired action present.
    const same =
      existing.length === desired.length &&
      desired.every((a) => existing.includes(a));
    if (same) return;
    setValue(
      'permissions',
      { ...current, epcProject: desired },
      { shouldDirty: true, shouldValidate: true },
    );
  }, [watchedTag, activeDept, getValues, setValue]);

  // ── Department-switch handler: clear permissions + approver to avoid
  // cross-department pollution.
  const handleDeptChange = useCallback(
    (newDept: DeptKey) => {
      if (newDept === activeDept) return;
      setActiveDept(newDept);
      setValue('permissions', {}, { shouldDirty: true, shouldValidate: true });
      setSelectedApproverRole('');
      // tag only applies to EPC roles. Clearing on switch keeps the form
      // honest so leaving EPC → OMS doesn't smuggle a stale tag into the
      // request body.
      if (newDept !== 'EPC') {
        setValue('tag', undefined, { shouldDirty: true });
      }
    },
    [activeDept, setValue],
  );

  // ── Approval-chain walk (multi-level). Mirrors RoleForm exactly.
  const editingRoleId = (editingRole?._id || editingRole?.id || '') as string;
  const approvalChain = useMemo<ApprovalChainState>(() => {
    const steps: Role[] = [];
    if (!selectedApproverRole) {
      return { steps, cycle: false, terminalReached: true };
    }
    const visited = new Set<string>();
    if (editingRoleId) visited.add(editingRoleId);
    let nextId: string | undefined = selectedApproverRole;
    const maxHops = approverRoles.length + 1;
    for (let i = 0; i < maxHops && nextId; i++) {
      if (visited.has(nextId)) {
        return { steps, cycle: true, terminalReached: false };
      }
      visited.add(nextId);
      const role = approverRoles.find((r) => (r._id || r.id) === nextId);
      if (!role) {
        return { steps, cycle: false, terminalReached: false };
      }
      steps.push(role);
      nextId = role.approverRoleId || undefined;
    }
    return { steps, cycle: false, terminalReached: !nextId };
  }, [selectedApproverRole, approverRoles, editingRoleId]);

  // ── Fetch approver roles whenever the active department changes.
  useEffect(() => {
    const fetchApproverRoles = async () => {
      try {
        const roles = await RoleService.getApproverRoles(activeDept);
        setApproverRoles(roles);
      } catch (error) {
        console.error('Failed to fetch approver roles:', error);
        toast.error('Failed to load approver roles');
      }
    };

    fetchApproverRoles();
  }, [activeDept]);

  // ── Auto-fill form when editing an existing role.
  useEffect(() => {
    if (editingRole && permissionsConfig) {
      // Permissions may arrive as a Map (Mongoose) or plain object.
      let permissionsObj: Record<string, string[]> = (editingRole.permissions ||
        {}) as Record<string, string[]>;
      if (permissionsObj instanceof Map) {
        permissionsObj = Object.fromEntries(permissionsObj) as Record<string, string[]>;
      }

      const roleType = editingRole.roleType || 'both';
      const approverRoleId = editingRole.approverRoleId || '';

      setSelectedRoleType(roleType);
      setSelectedApproverRole(approverRoleId);
      setIsMobileUser(editingRole.isMobileUserRole || false);

      reset(
        {
          name: editingRole.name,
          description: editingRole.description || '',
          permissions: permissionsObj,
          roleType: roleType,
          approverRoleId: approverRoleId,
          requiresApprovalFor: editingRole.requiresApprovalFor || [],
          isMobileUserRole: editingRole.isMobileUserRole || false,
          // Carry the EPC tier tag so the picker reflects the persisted value
          // and the next PATCH keeps the role tagged.
          ...(editingRole.tag ? { tag: editingRole.tag } : {}),
        },
        {
          keepDirty: false,
          keepTouched: false,
          keepIsValid: false,
          keepIsSubmitted: false,
          keepSubmitCount: false,
        },
      );
      setFocus('name');
    }
  }, [editingRole, permissionsConfig, reset, setFocus]);

  // ── Submit handler — port of RoleForm.onSubmit, behaviour-preserving.
  const onSubmit = useCallback(
    async (data: RoleEditorFormValues) => {
      setApiError(null);
      setShowSuccess(false);

      // Default (system) roles are fully locked — the backend rejects every
      // change; only a direct database update can lift the lock.
      if ((editingRole as { isDefault?: boolean } | null | undefined)?.isDefault) {
        toast.info(
          `"${editingRole?.name}" is a default role — it is locked and cannot be changed from the application.`,
        );
        return;
      }

      try {
        // Validate approval workflow — requester / both must have an approver.
        if (
          !isMobileUser &&
          (selectedRoleType === 'requester' || selectedRoleType === 'both') &&
          !selectedApproverRole
        ) {
          setApiError('Please select an approver role for requester roles');
          toast.error('Please select an approver role for requester roles');
          return;
        }

        // Block circular approval mappings.
        if (selectedApproverRole && approvalChain.cycle) {
          const msg =
            "Circular approval mapping detected. Pick an approver whose own chain doesn't lead back to this role.";
          setApiError(msg);
          toast.error(msg);
          return;
        }

        // EPC roles must carry a tier tag — backend Joi validator hard-rejects
        // any EPC role create/update without one. Surface a friendly message
        // here so users don't see a generic 422 toast.
        if (activeDept === 'EPC' && !data.tag) {
          const msg =
            'Pick an EPC role tier (Acts as) — required for every EPC role.';
          setApiError(msg);
          toast.error(msg);
          return;
        }

        // Strict sanitisation: drop any (module, action) the live config does
        // NOT declare. Mirrors the backend validator.
        const { clean: filteredPermissions, stripped } = sanitizePermissions(
          (data.permissions as Record<string, string[]> | undefined) ?? {},
          rawPermissionsConfig,
        );

        if (stripped.length > 0) {
          toast.info(
            `Cleaned up ${stripped.length} stale grant${stripped.length === 1 ? '' : 's'} before saving: ${summarizeStripped(stripped)}`,
            { autoClose: 5000 },
          );
        }

        // Mobile-user roles sit outside the role-level approval workflow —
        // approvals are decided per project, so roleType/approverRoleId must
        // not be sent at all (the backend rejects them with 400).
        const {
          roleType: _formRoleType,
          approverRoleId: _formApproverRoleId,
          ...restData
        } = data;
        const roleData = {
          ...restData,
          permissions: filteredPermissions,
          ...(isMobileUser
            ? {}
            : {
                roleType: selectedRoleType,
                approverRoleId: selectedApproverRole || undefined,
              }),
          requiresApprovalFor: data.requiresApprovalFor || [],
          department: activeDept,
          isMobileUserRole: isMobileUser,
          // Only forward tag for EPC roles. Sending it for OMS/AMS would be
          // accepted by Joi (optional) but is unnecessary noise.
          ...(activeDept === 'EPC' && data.tag ? { tag: data.tag } : {}),
        };

        const response = editingRole
          ? await RoleService.updateRole(
              (editingRole.id || editingRole._id) as string,
              roleData,
            )
          : await RoleService.createRole(roleData);

        setShowSuccess(true);
        toast.success(
          editingRole ? 'Role updated successfully!' : 'Role created successfully!',
        );

        setSavedRole(response);
        setPostSavePrompt(true);
        onAdd(response);
      } catch (err) {
        const error = err as AxiosError<{ message?: string }>;
        const message =
          error.response?.data?.message ||
          error.message ||
          'Something went wrong. Please try again.';

        setApiError(message);
        console.error('Role Submit Error:', error);
      }
    },
    [
      isMobileUser,
      selectedRoleType,
      selectedApproverRole,
      approvalChain.cycle,
      rawPermissionsConfig,
      activeDept,
      editingRole,
      onAdd,
    ],
  );

  // The bound submit fn for the footer to call.
  const submit = useCallback(() => handleSubmit(onSubmit)(), [handleSubmit, onSubmit]);

  const clearApiError = useCallback(() => setApiError(null), []);

  // ── Readonly when editing a default (system) role.
  const isReadonly = !!(editingRole as { isDefault?: boolean } | null | undefined)?.isDefault;

  const formTitle = editingRole ? 'Edit Role' : 'Create New Role';

  // ── Composable context value (everything outside RHF state).
  const ctxValue = useMemo<RoleEditorContextValue>(
    () => ({
      editingRole: editingRole ?? null,
      isReadonly,
      isMobileUser,
      setIsMobileUser,
      activeDept,
      setActiveDept: handleDeptChange,
      approverRoles,
      selectedApproverRole,
      setSelectedApproverRole,
      selectedRoleType,
      setSelectedRoleType,
      approvalChain,
      matrixConfig,
      isLoadingConfig,
      activeSection,
      setActiveSection,
    }),
    [
      editingRole,
      isReadonly,
      isMobileUser,
      activeDept,
      handleDeptChange,
      approverRoles,
      selectedApproverRole,
      selectedRoleType,
      approvalChain,
      matrixConfig,
      isLoadingConfig,
      activeSection,
    ],
  );

  // Command surface for header/footer siblings (submit, isSubmitting, etc).
  const commandValue = useMemo<RoleEditorCommandValue>(
    () => ({
      submit,
      isSubmitting,
      isDirty,
      apiError,
      clearApiError,
      showSuccess,
      postSavePrompt,
      setPostSavePrompt,
      savedRole,
      setActiveSection,
      activeSection,
      formTitle,
      editingRole: editingRole ?? null,
    }),
    [
      submit,
      isSubmitting,
      isDirty,
      apiError,
      clearApiError,
      showSuccess,
      postSavePrompt,
      savedRole,
      activeSection,
      formTitle,
      editingRole,
    ],
  );

  // Build the tree with React.createElement so this file stays .ts (no JSX).
  // FormProvider's props are `UseFormReturn & { children }`. We can't use the
  // JSX type-inference machinery here, so cast the props object to the
  // generic Component type expected by createElement. This is the only
  // unavoidable cast for a .ts-only Provider wrapper.
  const commandTree = createElement(
    RoleEditorCommandContext.Provider,
    { value: commandValue },
    children,
  );
  const contextTree = createElement(
    RoleEditorContext.Provider,
    { value: ctxValue },
    commandTree,
  );
  return createElement(
    FormProvider as ComponentType<{ children: ReactNode }>,
    { ...(form as unknown as object), children: contextTree } as { children: ReactNode },
  );
}

export default RoleEditorProvider;
